import React, { useState, useEffect, FC } from "react";
import { Button as RButton, Badge as RBadge } from "reactstrap";
import {
  Table,
  Input,
  Flex,
  Spin,
  Tooltip,
  Drawer,
  Modal,
  Form,
  Popover,
  Timeline,
  Select,
  DatePicker,
  Space,
  Tag,
  Empty,
  Descriptions,
  Divider,
  Card,
  Statistic,
  Button,
  Badge,
  Row,
  Col,
} from "antd";
import { Icon, TooltipComponent } from "../../components/Component";
import FormatDate from "../masters/DateFormater";
import {
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  FileTextOutlined,
  DollarOutlined,
  CalendarOutlined,
  SearchOutlined,
  EditOutlined,
  CopyOutlined,
  FilePdfOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router";
import PaymentsAside from "./PaymentsAside";
import paymentApis from "../../api/WorkOrderManagement/PaymentsApis";
import { formatCurrency } from "../../utils/Utils";
import dayjs from "dayjs";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import PaymentBulkUpload from "../../components/upload/PaymentBulkUpload";
import {
  PaymentData,
  PaymentStatus,
  PaymentType,
  PaymentMethod,
  UserRole,
  PaymentFilters,
  PaymentWidgetData,
} from "./types";
import {
  mockPaymentData,
  getPaymentsByStatus,
  getPaymentsByContractor,
  mockPaymentWidgetData,
  mockPaymentStatusOptions,
  mockPaymentMethodOptions,
} from "./mockData/paymentData";
import {
  mockContractorOptions,
  mockSiteOptions,
} from "./mockData/approvedRaBillsData";
import { recordStats } from "framer-motion";
import PaymentPdfGenerator from "./PaymentPdfGenerator";
import { useSelector } from "react-redux";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";
import { useSearchParams } from "react-router-dom";

// Filter State Interface
interface FilterState {
  siteId?: string[];
  contractorName?: string[];
  workOrderId?: string[];
  raBillId?: string[];
}

const ViewPayments: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flowType = searchParams.get("flowType");
  const id = location?.state?.id;
  const [widgetData, setWidgetData] = useState<any>([]);
  const [sm, setSm] = useState(false);
  const [drawerData, setDrawerData] = useState<any>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);

  // Enhanced payment management state
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<PaymentData[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentData[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [paymentWidgetData, setPaymentWidgetData] = useState<PaymentWidgetData>(
    mockPaymentWidgetData
  );

  // Filters
  const [filters, setFilters] = useState<PaymentFilters>({});
  const [searchText, setSearchText] = useState("");

  // Advanced Filters State
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [filterForm] = Form.useForm();
  const [contractorOptions, setContractorOptions] = useState<any[]>([]);
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [workOrderOptions, setWorkOrderOptions] = useState<any[]>([]);
  const [raBillOptions, setRABillOptions] = useState<any[]>([]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const isClientFlow = flowType?.toLowerCase() === "client";

  // PDF Generation state
  const [pdfPaymentData, setPdfPaymentData] = useState<any>(null);
  const [showPdfGenerator, setShowPdfGenerator] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Fetch site options from currentUser
  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(advancedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // Filter Functions
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...advancedFilters });
    filterForm.setFieldsValue(advancedFilters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const flowTypeParam = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const partyType = isClientFlow ? "Client" : "Contractor";
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || "N/A",
          value: item.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch work orders
      const workOrdersResponse = await dropdownApis.fetchWorkOrderDropdown(
        flowTypeParam
      );
      if (
        workOrdersResponse &&
        workOrdersResponse.status &&
        workOrdersResponse.data
      ) {
        const workOrderOptions = workOrdersResponse.data.map((item: any) => {
          const label =
            item.workOrderNumber ||
            item.workOrderSequenceId ||
            item.sequenceId ||
            item.workOrderTitle ||
            `WO-${item.workOrderId || item.id || "N/A"}`;
          const value = item.workOrderId?.toString() || item.id?.toString();
          return { label, value };
        });
        setWorkOrderOptions(workOrderOptions);
      }

      // Fetch RA Bills
      const raBillsResponse = await dropdownApis.fetchRABillDropdown(
        flowTypeParam
      );
      if (raBillsResponse && raBillsResponse.status && raBillsResponse.data) {
        const raBillOptions = raBillsResponse.data.map((item: any) => ({
          label: item.raBillSequenceId || `RA-${item.raBillId || "N/A"}`,
          value: item.raBillId?.toString(),
        }));
        setRABillOptions(raBillOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleApplyFilters = () => {
    filterForm
      .validateFields()
      .then((values) => {
        const processedFilters: FilterState = {
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          workOrderId:
            values.workOrderId && values.workOrderId.length > 0
              ? values.workOrderId
              : undefined,
          raBillId:
            values.raBillId && values.raBillId.length > 0
              ? values.raBillId
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        // Always create a new object reference to trigger useEffect
        setAdvancedFilters({ ...processedFilters });
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setAdvancedFilters({});
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName]: { ...advancedFilters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setAdvancedFilters(savedFilter);
      filterForm.setFieldsValue(savedFilter);
    }
  };

  // Enhanced useEffect hooks
  // useEffect(() => {
  //   loadPayments();
  // }, []);

  // const loadPayments = async () => {
  //   try {
  //     setLoading(true);

  //     // Simulate API call
  //     await new Promise((resolve) => setTimeout(resolve, 500));

  //     // Load all payments
  //     setPayments(mockPaymentData);
  //     setPaymentWidgetData(mockPaymentWidgetData);
  //   } catch (error) {
  //     console.error("Error loading payments:", error);
  //     Swal.fire("Error!", "Failed to load payments", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Handle opening payment modal from notification navigation
  useEffect(() => {
    const paymentId = location?.state?.id;
    if (paymentId) {
      // Create a temporary payment object with the ID to open the modal
      const tempPayment = {
        raBillPaymentId: paymentId,
      };
      handleViewPayment(tempPayment);
      // Clear the state to prevent reopening on re-render
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.id]);

  const getStatusColor = (status: PaymentStatus): string => {
    const colors: Record<PaymentStatus, string> = {
      [PaymentStatus.Draft]: "default",
      [PaymentStatus.Submitted]: "processing",
      [PaymentStatus.Checked]: "warning",
      [PaymentStatus.Verified]: "orange",
      [PaymentStatus.Approved]: "success",
      [PaymentStatus.Posted]: "cyan",
      [PaymentStatus.Paid]: "green",
      [PaymentStatus.Rejected]: "error",
      [PaymentStatus.Returned]: "purple",
    };
    return colors[status] || "default";
  };

  const handleViewPayment = async (payment: any) => {
    try {
      setLoading(true);

      // Call the paymentById API with the raBillPaymentId
      const response: any = await paymentApis.paymentById(
        payment.raBillPaymentId
      );

      console.log("Payment details response:", response);

      const { data, status, message } = response;

      if (status) {
        // Process the API response and map it to the expected payment structure
        const processedPayment = {
          ...payment,
          // Map API response fields to payment object
          paymentVoucherNumber: data.raBillPaymentSequenceId,
          paymentDate: data.paymentDate,
          paymentType: data.paymentType,
          status: data.status,
          contractorName: data.contractorName,
          siteName: data.siteName,
          paymentMethod: data.paymentMethod,
          bankAccountNumber: data.bankAccountNumber,
          bankName: data.bankName,
          totalPaymentAmount: data.totalPaymentAmount,
          paidAmount: data.paidAmount,
          netPayableAmount: data.netPayableAmount,
          tdsSection: data.tdsSection,
          tdsRate: data.tdsRate,
          tdsDeducted: data.tdsDeducted,
          otherDeductions: data.otherDeductions,
          utrReference:
            data.utrNumber || data.chequeNumber || data.transactionNumber,
          narration: data.narration,
          remarks: data.remarks,

          // Process payment lines (allocations)
          allocations:
            data.raBillPaymentLines?.map((line: any) => ({
              id: line.raBillPaymentLineId,
              raBillId: line.raBillId,
              raBillNumber: line.raBillSequenceId,
              workOrderId: line.workOrderId,
              raPeriodFrom: line.raPeriodFrom,
              raPeriodTo: line.raPeriodTo,
              paymentStatus: line.paymentStatus,
              totalPaidAmount: line.totalPaidAmount,
              paidAmount: line.paidAmount,
              raBillNetPayable: line.raBillNetPayable,
              workOrderNumber: line.workOrderSequenceId || line.workOrderNumber,
              thisPaymentAmount: line.paidAmount,
              resultingStatus: line.status,
            })) || [],

          // Process approval history
          approvalHistory:
            data.paymentApprovals?.map((approval: any) => ({
              action:
                approval.status === "Approved"
                  ? "Approved"
                  : approval.status === "Rejected"
                  ? "Rejected"
                  : "Pending",
              actionBy: approval.approvedByName || approval.approverName,
              actionDate: approval.approvalDate || approval.createdAt,
              role: approval.approvalStage,
              status: approval.status,
              comments: approval.reason,
              level: approval.level,
            })) || [],

          // Process attachments/documents
          attachments: data.dms || [],

          // Additional details
          createdBy: data.createdByName,
          createdAt: data.createdAt,
          processedBy: data.processedByName,
          processedAt: data.processedAt,
          approvedBy: data.approvedByName,
          approvedAt: data.approvedAt,
        };

        setSelectedPayment(processedPayment);
        setShowDetailsModal(true);
      } else {
        Swal.fire(
          "Error!",
          message || "Failed to fetch payment details",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error fetching payment details:", error);
      Swal.fire(
        "Error!",
        "Failed to fetch payment details. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF Export
  const handleExportPdf = async (payment: any) => {
    try {
      setPdfLoading(true);

      // Call the paymentById API with the raBillPaymentId
      const response: any = await paymentApis.paymentById(
        payment.raBillPaymentId
      );

      console.log("Payment details for PDF:", response);

      const { data, status, message } = response;

      if (status) {
        // Process the API response and map it to the expected payment structure
        const processedPayment = {
          ...payment,
          paymentVoucherNumber: data.raBillPaymentSequenceId,
          paymentDate: data.paymentDate,
          paymentType: data.paymentType,
          status: data.status,
          contractorName: data.contractorName,
          siteName: data.siteName,
          paymentMethod: data.paymentMethod,
          bankAccountNumber: data.bankAccountNumber,
          bankName: data.bankName,
          totalPaymentAmount: data.totalPaymentAmount,
          paidAmount: data.paidAmount,
          netPayableAmount: data.netPayableAmount,
          tdsSection: data.tdsSection,
          tdsRate: data.tdsRate,
          tdsDeducted: data.tdsDeducted,
          otherDeductions: data.otherDeductions,
          utrReference:
            data.utrNumber || data.chequeNumber || data.transactionNumber,
          narration: data.narration,
          remarks: data.remarks,
          allocations:
            data.raBillPaymentLines?.map((line: any) => ({
              id: line.raBillPaymentLineId,
              raBillId: line.raBillId,
              raBillNumber: line.raBillSequenceId,
              workOrderId: line.workOrderId,
              raPeriodFrom: line.raPeriodFrom,
              raPeriodTo: line.raPeriodTo,
              paymentStatus: line.paymentStatus,
              totalPaidAmount: line.totalPaidAmount,
              paidAmount: line.paidAmount,
              raBillNetPayable: line.raBillNetPayable,
              workOrderNumber: line.workOrderSequenceId || line.workOrderNumber,
              thisPaymentAmount: line.paidAmount,
              resultingStatus: line.status,
            })) || [],
          approvalHistory:
            data.paymentApprovals?.map((approval: any) => ({
              action:
                approval.status === "Approved"
                  ? "Approved"
                  : approval.status === "Rejected"
                  ? "Rejected"
                  : "Pending",
              actionBy: approval.approvedByName || approval.approverName,
              actionDate: approval.approvalDate || approval.createdAt,
              role: approval.approvalStage,
              status: approval.status,
              comments: approval.reason,
              level: approval.level,
            })) || [],
          attachments: data.dms || [],
          createdBy: data.createdByName,
          createdAt: data.createdAt,
          processedBy: data.processedByName,
          processedAt: data.processedAt,
          approvedBy: data.approvedByName,
          approvedAt: data.approvedAt,
        };

        setPdfPaymentData(processedPayment);
        setShowPdfGenerator(true);

        // Trigger PDF generation after a short delay to ensure state is updated
        setTimeout(() => {
          const pdfTrigger = document.querySelector(
            "[data-payment-pdf-trigger]"
          ) as HTMLButtonElement;
          if (pdfTrigger) {
            pdfTrigger.click();
          }
        }, 100);
      } else {
        Swal.fire(
          "Error!",
          message || "Failed to fetch payment details for PDF",
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error fetching payment details for PDF:", error);
      Swal.fire("Error!", "Failed to generate PDF. Please try again.", "error");
      setPdfLoading(false);
    }
  };

  // Enhanced columns for new payment data
  const enhancedColumns: any = [
    // {
    //   title: "#",
    //   key: "#",
    //   width: 50,
    //   render: (_: any, __: any, index: number) => index + 1,
    // },
    {
      title: "Payment Voucher",
      dataIndex: "raBillPaymentSequenceId",
      key: "raBillPaymentSequenceId",
      width: 260,
      fixed: "left",
      render: (text: string, record: PaymentData) => (
        <div>
          <div className="fw-bold text-primary">{text}</div>
          <small className="text-muted">{record.paymentType} Payment</small>
        </div>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 120,
      render: (date: string) => dayjs(date).format("DD-MMM-YYYY"),
    },
    {
      title: "Contractor",
      key: "contractor",
      width: 200,
      render: (record: PaymentData) => (
        <div>
          <div className="fw-bold">{record.contractorName}</div>
          <small className="text-muted">{record.siteName}</small>
        </div>
      ),
    },
    // {
    //   title: "Payment Method",
    //   dataIndex: "paymentMethod",
    //   key: "paymentMethod",
    //   width: 100,
    //   render: (method: PaymentMethod) => <Tag color="blue">{method}</Tag>,
    // },
    {
      title: "Net Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 120,
      render: (amount: number) => (
        <div className="fw-bold text-success">₹{amount?.toLocaleString()}</div>
      ),
    },
    {
      title: "Reference #",
      dataIndex: "utrNumber",
      key: "utrNumber",
      width: 200,
      render: (utr: string, record: any) => {
        const reference =
          record.utrNumber ||
          record.transactionNumber ||
          record.chequeNumber ||
          "N/A";
        return (
          <div>
            <div className="fw-bold">{reference}</div>
            <small className="text-muted">{record.paymentMethod} Payment</small>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status: PaymentStatus) => (
        <Tag color={getStatusColor(status)}>{status}</Tag>
      ),
    },
    // {
    //   title: "Payment Type",
    //   dataIndex: "paymentType",
    //   key: "paymentType",
    //   width: 180,
    // },
    // {
    //   title: "RA Bills",
    //   key: "raBills",
    //   width: 80,
    //   render: (record: PaymentData) => (
    //     <span className="badge badge-dim badge-blue">
    //       {record.allocations?.length}
    //     </span>
    //   ),
    // },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (record: PaymentData) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewPayment(record)}
            />
          </Tooltip>
          <Tooltip title="Export PDF">
            <Button
              type="text"
              icon={<FilePdfOutlined />}
              onClick={() => handleExportPdf(record)}
            />
          </Tooltip>
          {/* {record.status === PaymentStatus.Draft && (
            <Tooltip title="Edit">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEditPayment(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Duplicate">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleDuplicatePayment(record)}
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  const ApprovalTimelineContent = (approvalsData: any) => {
    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No Approvals</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status == "Approved";
              const isRejected = approval.status == "Rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.userName}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.approvalDate && (
                    <div className="designation">
                      {dayjs(approval?.approvalDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            color: "#2263b3",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          <RBadge color="outline-success">View</RBadge>
        </span>
      </Popover>
    );
  };

  const disapprovePayment = async (reason: any) => {
    // try {
    //   const res = await paymentApis.disapprovePayment(
    //     drawerData?.paymentNumber,
    //     reason
    //   );
    //   const { status, message } = res;
    //   if (status) {
    //     Swal.fire("Success", message, "success");
    //     // fetchData();
    //   }
    // } catch (error) {}
  };

  const { Search } = Input;
  const { RangePicker } = DatePicker;
  const { Option } = Select;

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="view-payments"
        title="All Payments"
        columns={enhancedColumns} // Use enhanced columns
        loading={loading}
        customFilters={advancedFilters}
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
            <PaymentBulkUpload showAsButton={true} flowType="Contractor" />
          </Space>
        }
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <PaymentsAside updateSm={setSm} sm={sm} />
          </div>
        }
        extra={{
          tableScrollX: 1200,
          excelExport: true,
        }}
      />

      {/* Advanced Filters Drawer */}
      <Drawer
        title="Advanced Filters"
        placement="right"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <Button size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Select.Option key={filterName} value={filterName}>
                      {filterName}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={filterForm}
          layout="vertical"
          initialValues={advancedFilters}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="siteId" label="Site/Project">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderId" label="Work Order Number">
                <Select
                  mode="multiple"
                  placeholder="Select Work Orders"
                  options={workOrderOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="raBillId" label="RA Bill">
                <Select
                  mode="multiple"
                  placeholder="Select RA Bills"
                  options={raBillOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      {/* Enhanced Payment Details Modal */}
      <Modal
        title={
          <div className="d-flex align-items-center">
            <FileTextOutlined className="mr-2" />
            Payment Details - {selectedPayment?.paymentVoucherNumber}
          </div>
        }
        open={showDetailsModal}
        onCancel={() => setShowDetailsModal(false)}
        width={1500}
        footer={[
          <Button key="close" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>,
        ].filter(Boolean)}
      >
        {selectedPayment && (
          <div>
            <Row gutter={[24, 24]}>
              <Col span={20}>
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Payment Voucher">
                    {selectedPayment.paymentVoucherNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Date">
                    {dayjs(selectedPayment.paymentDate).format("DD-MMM-YYYY")}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment Type">
                    {selectedPayment.paymentType}
                  </Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={getStatusColor(selectedPayment.status)}>
                      {selectedPayment.status}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Contractor">
                    {selectedPayment.contractorName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Site">
                    {selectedPayment.siteName}
                  </Descriptions.Item>
                  {/* <Descriptions.Item label="Legal Entity">
                    {selectedPayment.legalEntityName}
                  </Descriptions.Item> */}
                  <Descriptions.Item label="Payment Method">
                    {selectedPayment.paymentMethod}
                  </Descriptions.Item>
                  {/* <Descriptions.Item label="Bank Account">
                    {selectedPayment.bankAccountNumber &&
                    selectedPayment.bankName
                      ? `${selectedPayment.bankAccountNumber} - ${selectedPayment.bankName}`
                      : selectedPayment.bankAccountName || "N/A"}
                  </Descriptions.Item> */}
                  <Descriptions.Item label="TDS Section">
                    {selectedPayment.tdsSection} ({selectedPayment.tdsRate}%)
                  </Descriptions.Item>
                  <Descriptions.Item label="TDS Deducted">
                    ₹{selectedPayment.tdsDeducted?.toLocaleString() || "0"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Other Deductions">
                    ₹{selectedPayment.otherDeductions?.toLocaleString() || "0"}
                  </Descriptions.Item>
                  {selectedPayment.utrReference &&
                    selectedPayment.paymentMethod !== "Cash" && (
                      <Descriptions.Item
                        label={
                          selectedPayment.paymentMethod == "Cheque"
                            ? "Cheque Number"
                            : selectedPayment.paymentMethod == "NEFT" ||
                              selectedPayment.paymentMethod == "RTGS" ||
                              selectedPayment.paymentMethod == "IMPS"
                            ? "UTR Number"
                            : "Transaction Number"
                        }
                        span={2}
                      >
                        <code>{selectedPayment.utrReference}</code>
                      </Descriptions.Item>
                    )}
                  <Descriptions.Item label="Narration" span={2}>
                    {selectedPayment.narration || "N/A"}
                  </Descriptions.Item>
                  {/* {selectedPayment.remarks && (
                    <Descriptions.Item label="Remarks" span={2}>
                      {selectedPayment.remarks}
                    </Descriptions.Item>
                  )} */}
                  <Descriptions.Item label="Created By">
                    {selectedPayment.createdBy}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {selectedPayment.createdAt
                      ? dayjs(selectedPayment.createdAt).format(
                          "DD-MMM-YYYY HH:mm"
                        )
                      : "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title="Net Payable Amount"
                    value={
                      selectedPayment.netPayableAmount ||
                      selectedPayment.netPayable
                    }
                    precision={2}
                    prefix="₹"
                    valueStyle={{ color: "#3f8600" }}
                  />
                  <Divider />
                  <div className="d-flex justify-content-between">
                    <span>Total Payment Amount:</span>
                    <strong>
                      ₹{selectedPayment.totalPaymentAmount?.toLocaleString()}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Paid Amount:</span>
                    <strong>
                      ₹{selectedPayment.paidAmount?.toLocaleString()}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>TDS Deducted:</span>
                    <strong className="text-warning">
                      -₹{selectedPayment.tdsDeducted?.toLocaleString() || "0"}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Other Deductions:</span>
                    <strong>
                      -₹
                      {selectedPayment.otherDeductions?.toLocaleString() || "0"}
                    </strong>
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider />

            <h6>
              Payment Allocations ({selectedPayment?.allocations?.length})
            </h6>
            <Table
              columns={[
                {
                  title: "RA Bill Number",
                  dataIndex: "raBillNumber",
                  key: "raBillNumber",
                  width: 140,
                },
                {
                  title: "Work Order",
                  dataIndex: "workOrderNumber",
                  key: "workOrderNumber",
                  width: 140,
                },
                {
                  title: "RA Period",
                  dataIndex: "raPeriod",
                  key: "raPeriod",
                  width: 180,
                  render: (data, record: any) =>
                    `${
                      record?.raPeriodFrom &&
                      dayjs(record.raPeriodFrom).format("DD-MMM-YYYY")
                    } to ${
                      record?.raPeriodTo &&
                      dayjs(record.raPeriodTo).format("DD-MMM-YYYY")
                    }`,
                },
                {
                  title: "Bill Value",
                  dataIndex: "raBillNetPayable",
                  key: "raBillNetPayable",
                  width: 120,
                  render: (amount: number) => `₹${amount?.toLocaleString()}`,
                },
                {
                  title: "Already Paid",
                  dataIndex: "totalPaidAmount",
                  key: "totalPaidAmount",
                  width: 120,
                  render: (amount: number, record: any) =>
                    `₹${record.totalPaidAmount}`,
                },
                {
                  title: "This Payment",
                  dataIndex: "paidAmount",
                  key: "paidAmount",
                  width: 120,
                  render: (amount: number, record: any) =>
                    `₹${amount?.toLocaleString()}`,
                },
                {
                  title: "Payment Status",
                  dataIndex: "paymentStatus",
                  key: "paymentStatus",
                  width: 120,
                  render: (status: string) => {
                    const color =
                      status === "Paid"
                        ? "green"
                        : status === "Partially Paid"
                        ? "orange"
                        : "red";
                    return <Tag color={color}>{status}</Tag>;
                  },
                },
              ]}
              dataSource={selectedPayment.allocations}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: 800 }}
            />

            <Divider />

            {/* Attachments Section */}
            {selectedPayment.attachments &&
              selectedPayment.attachments.length > 0 && (
                <>
                  <h6>
                    Supporting Documents ({selectedPayment.attachments.length})
                  </h6>
                  <div style={{ marginBottom: 16 }}>
                    {selectedPayment.attachments.map(
                      (attachment: any, index: number) => (
                        <Card
                          key={index}
                          size="small"
                          style={{ marginBottom: 8 }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <FileTextOutlined className="mr-2" />
                              <strong>
                                {attachment.fileName ||
                                  attachment.originalFileName ||
                                  `Document ${index + 1}`}
                              </strong>
                              {attachment.fileSize && (
                                <span className="text-muted ml-2">
                                  ({(attachment.fileSize / 1024).toFixed(1)} KB)
                                </span>
                              )}
                            </div>
                            <div>
                              {attachment.uploadDate && (
                                <span className="text-muted mr-2">
                                  {dayjs(attachment.uploadDate).format(
                                    "DD/MM/YYYY"
                                  )}
                                </span>
                              )}
                              {attachment.fileUrl && (
                                <Button
                                  size="small"
                                  type="link"
                                  icon={<EyeOutlined />}
                                  onClick={() =>
                                    window.open(attachment.fileUrl, "_blank")
                                  }
                                >
                                  View
                                </Button>
                              )}
                            </div>
                          </div>
                        </Card>
                      )
                    )}
                  </div>
                  <Divider />
                </>
              )}

            <h6>Approval History</h6>
            <Timeline>
              {selectedPayment.approvalHistory.map(
                (history: any, index: any) => (
                  <Timeline.Item
                    key={index}
                    color={
                      history.status === PaymentStatus.Rejected ? "red" : "blue"
                    }
                  >
                    <div>
                      <strong>{history.action}</strong> by {history.actionBy}
                      <div className="text-muted small">
                        {dayjs(history.actionDate).format("DD/MM/YYYY HH:mm")} |{" "}
                        {history.role}
                      </div>
                      {history.comments && (
                        <div className="mt-1 p-2 bg-light rounded">
                          <small>{history.comments}</small>
                        </div>
                      )}
                    </div>
                  </Timeline.Item>
                )
              )}
            </Timeline>
          </div>
        )}
      </Modal>
      <DisapprovePayment
        isModalVisible={showRejectModal}
        setIsModalVisible={setShowRejectModal}
        reject={disapprovePayment}
      />

      {/* PDF Loading Modal */}
      <Modal
        open={pdfLoading}
        footer={null}
        closable={false}
        centered
        width={300}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px", fontSize: "14px", color: "#666" }}>
            Generating PDF Preview...
          </div>
        </div>
      </Modal>

      {/* Hidden PDF Generator - triggered from action column */}
      {showPdfGenerator && pdfPaymentData && (
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            zIndex: -1000,
          }}
        >
          <PaymentPdfGenerator
            paymentData={pdfPaymentData}
            orgName={pdfPaymentData?.contractorName || "Organization"}
            triggerAttribute="data-payment-pdf-trigger"
            onPdfGenerated={() => setPdfLoading(false)}
          />
        </div>
      )}
    </>
  );
};

const DisapprovePayment = ({
  isModalVisible,
  setIsModalVisible,
  reject,
}: any) => {
  const [form] = Form.useForm();

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const reason = values.reason;
        reject(reason); // Call your reject function here
        form.resetFields();
        setIsModalVisible(false);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <Modal
      title="Reason for Rejection"
      open={isModalVisible}
      footer={null} // Remove the default footer buttons
      onCancel={handleCancel}
    >
      <Form
        form={form}
        layout="vertical"
        name="rejectForm"
        requiredMark={false}
      >
        <Form.Item
          name="reason"
          label={undefined}
          rules={[{ required: true, message: "Please input your reason!" }]}
        >
          <Input.TextArea placeholder="Type your reason here..." />
        </Form.Item>
        <div className="actions clearfix">
          <ul>
            <li>
              <RButton color="primary" onClick={handleOk}>
                Submit
              </RButton>
            </li>
            <li>
              <RButton
                color="primary"
                onClick={handleCancel}
                className="btn-dim btn btn-secondary"
              >
                Cancel
              </RButton>
            </li>
          </ul>
        </div>
      </Form>
    </Modal>
  );
};

export default ViewPayments;
